import { useMemo, useState } from 'react';
import type { ModuleViewProps } from '../../shell/types';
import { makeIdentityT, type IdentityKey, type IdentityLocale } from './i18n';
import { CardView } from './card/view';
import { DuplicatesView } from './duplicates/view';
import { MergeView } from './merge/view';
import { ConflictView } from './conflict/view';
import { ConsentsView } from './consents/view';
import { SegmentsView } from './segments/view';
import { CompaniesView } from './companies/view';
import { RetentionView } from './retention/view';
import { allows } from '../../api';
import { Notice, ModulePage } from '../../shell/kit';
import { useDetail, useTab } from '../../shell/hooks';

type TabKey =
  | 'card'
  | 'companies'
  | 'duplicates'
  | 'similar'
  | 'merge'
  | 'conflict'
  | 'consents'
  | 'segments'
  | 'retention';

interface Tab {
  readonly key: TabKey;
  readonly label: IdentityKey;
  readonly needs: 'merge' | 'segments' | 'link' | 'view' | 'admin';
}

const TABS: readonly Tab[] = [
  // Карточка идёт первой: с неё начинается работа с клиентом, остальные вкладки
  // — разбор частных случаев.
  { key: 'card', label: 'identity.tab.card', needs: 'view' },
  { key: 'companies', label: 'identity.tab.companies', needs: 'view' },
  { key: 'duplicates', label: 'identity.tab.duplicates', needs: 'merge' },
  { key: 'similar', label: 'identity.tab.similar', needs: 'merge' },
  { key: 'merge', label: 'identity.tab.merge', needs: 'merge' },
  { key: 'conflict', label: 'identity.tab.conflict', needs: 'link' },
  { key: 'consents', label: 'identity.tab.consents', needs: 'view' },
  { key: 'segments', label: 'identity.tab.segments', needs: 'segments' },
  { key: 'retention', label: 'identity.tab.retention', needs: 'admin' },
];

const MERGE_ROLES = new Set(['network_admin']);
const SEGMENT_ROLES = new Set(['marketer', 'network_admin']);
const LINK_ROLES = new Set(['cashier', 'operator', 'site_manager', 'network_admin']);

export default function IdentityModule({ t: shellT, user, siteId, title }: ModuleViewProps) {
  const locale: IdentityLocale = user.locale === 'kk' ? 'kk' : 'ru';
  const t = useMemo(() => makeIdentityT(locale), [locale]);

  const roles = useMemo(() => new Set(user.bindings.map((b) => b.role)), [user.bindings]);
  const networkWide = useMemo(() => user.bindings.some((b) => b.siteId === null), [user.bindings]);

  const canMerge = networkWide && [...roles].some((r) => MERGE_ROLES.has(r));
  const canSegments = networkWide && [...roles].some((r) => SEGMENT_ROLES.has(r));
  const canLink = [...roles].some((r) => LINK_ROLES.has(r));

  const allowed = (tab: Tab): boolean => {
    if (tab.needs === 'merge') return canMerge;
    if (tab.needs === 'segments') return canSegments;
    if (tab.needs === 'link') return canLink;
    if (tab.needs === 'admin') return allows(user, 'admin.manage');
    return true;
  };

  const visible = TABS.filter(allowed);
  const [active, setActive] = useTab<TabKey>(
    'identity',
    visible.map((tab) => tab.key),
    visible[0]?.key ?? 'consents',
  );

  // Клиент карточки живёт в адресе: `#/identity/card/<id>` можно переслать.
  const [cardCustomerId, setCardCustomerId] = useDetail('identity', 'card');
  const current = visible.find((tab) => tab.key === active) ?? visible[0];

  // Выбранный клиент живёт на уровне модуля: раньше уход на соседнюю вкладку и
  // обратно стирал его, и двадцатишестисимвольный идентификатор приходилось
  // вводить заново.
  const [customerId, setCustomerId] = useState('');

  return (
    <ModulePage
      title={title}
      subtitle={shellT('shell.nav')}
      tabs={visible.map((tab) => ({ key: tab.key, label: t(tab.label) }))}
      activeTab={current?.key ?? active}
      onTab={setActive}
    >
      {visible.length === 0 ? (
        <Notice tone="warn">{t('identity.common.forbidden')}</Notice>
      ) : (
        <>
          {current?.key === 'card' ? (
            <CardView
              t={t}
              user={user}
              customerId={cardCustomerId}
              onCustomerChange={setCardCustomerId}
            />
          ) : null}
          {current?.key === 'companies' ? <CompaniesView t={t} user={user} /> : null}
          {current?.key === 'duplicates' ? (
            <DuplicatesView t={t} locale={locale} humanOnly={false} siteId={siteId} />
          ) : null}
          {current?.key === 'similar' ? (
            <DuplicatesView t={t} locale={locale} humanOnly siteId={siteId} />
          ) : null}
          {current?.key === 'merge' ? <MergeView t={t} locale={locale} allowed={canMerge} /> : null}
          {current?.key === 'conflict' ? <ConflictView t={t} locale={locale} /> : null}
          {current?.key === 'consents' ? (
            <ConsentsView
              t={t}
              locale={locale}
              canAnonymize={canMerge}
              canRecord={allows(user, 'consent.manage')}
              customerId={customerId}
              onCustomerChange={setCustomerId}
            />
          ) : null}
          {current?.key === 'segments' ? <SegmentsView t={t} locale={locale} /> : null}
          {current?.key === 'retention' ? <RetentionView t={t} /> : null}
        </>
      )}
    </ModulePage>
  );
}
